import React, { useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useAdminAuth } from '../context/AdminAuthContext';
import {
  LayoutDashboard, ShoppingCart, Package, Megaphone, Leaf,
  FileText, Users, Building2, MessageCircle, TrendingUp,
  Settings, LogOut, ChevronLeft, ChevronRight, ExternalLink,
  Shield, Menu, X, Mail, Tractor, BellRing, Landmark
} from 'lucide-react';

const AdminLayout = ({ children }) => {
  const [collapsed, setCollapsed] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();
  const { currentUser, logout, hasRole } = useAdminAuth();

  const isSuperAdmin = hasRole('super_admin');

  const navItems = [
    { label: 'Dashboard', icon: LayoutDashboard, path: '/admin/dashboard' },
    { label: 'Orders', icon: ShoppingCart, path: '/admin/orders' },
    { label: 'Products', icon: Package, path: '/admin/products' },
    { label: 'Wholesale', icon: Building2, path: '/admin/wholesale' },
    { label: 'Ads', icon: Megaphone, path: '/admin/ads' },
    { label: 'Ecosystem', icon: Leaf, path: '/admin/ecosystem' },
    { label: 'Blog', icon: FileText, path: '/admin/blog' },
    ...(isSuperAdmin ? [
      { label: 'Users', icon: Users, path: '/admin/users' },
      { label: 'Bank Accounts', icon: Building2, path: '/admin/bank-accounts' },
      { label: 'Chat', icon: MessageCircle, path: '/admin/chat' },
      { label: 'Investments', icon: TrendingUp, path: '/admin/investments' },
      { label: 'Farmers', icon: Tractor, path: '/admin/farmers' },
      { label: 'Loan Interest', icon: Landmark, path: '/admin/cooperative-loans' },
      { label: 'Email', icon: Mail, path: '/admin/email' },
      { label: 'Settings', icon: Settings, path: '/admin/settings' },
    ] : [])
    ,...((isSuperAdmin || hasRole('admin')) ? [
      { label: 'Notifications', icon: BellRing, path: '/admin/notifications' },
    ] : [])
  ];

  const isActive = (path) => location.pathname === path;

  const NavContent = () => (
    <div className="flex flex-col h-full">
      {/* Logo */}
      <div className="p-4 border-b border-gray-700 flex items-center justify-between">
        {!collapsed && (
          <div className="flex items-center gap-2">
            <div className="w-8 h-8 bg-green-500 rounded-lg flex items-center justify-center flex-shrink-0">
              <Shield className="h-4 w-4 text-white" />
            </div>
            <div className="min-w-0">
              <p className="text-sm font-bold text-white truncate">Daye Farm</p>
              <p className="text-[10px] text-gray-400">Admin Panel</p>
            </div>
          </div>
        )}
        {collapsed && (
          <div className="w-8 h-8 bg-green-500 rounded-lg flex items-center justify-center mx-auto">
            <Shield className="h-4 w-4 text-white" />
          </div>
        )}
      </div>

      {/* Nav Items */}
      <nav className="flex-1 py-3 overflow-y-auto">
        {navItems.map((item) => {
          const Icon = item.icon;
          const active = isActive(item.path);
          return (
            <Link
              key={item.path}
              to={item.path}
              onClick={() => setMobileOpen(false)}
              className={`flex items-center gap-3 mx-2 mb-0.5 px-3 py-2.5 rounded-lg text-sm transition-all ${
                active
                  ? 'bg-green-600 text-white font-medium'
                  : 'text-gray-300 hover:bg-gray-700/60 hover:text-white'
              }`}
              data-testid={`sidebar-${item.label.toLowerCase().replace(/\s+/g, '-')}`}
              title={collapsed ? item.label : undefined}
            >
              <Icon className="h-4.5 w-4.5 flex-shrink-0" />
              {!collapsed && <span>{item.label}</span>}
            </Link>
          );
        })}
      </nav>

      {/* User Info & Actions */}
      <div className="border-t border-gray-700 p-3">
        {!collapsed && (
          <div className="mb-2 px-2">
            <p className="text-xs text-gray-400 truncate">{currentUser?.email || currentUser?.username}</p>
            <p className="text-[10px] text-gray-500 capitalize">{currentUser?.role?.replace('_', ' ')}</p>
          </div>
        )}
        <Link
          to="/"
          target="_blank"
          className="flex items-center gap-2 px-3 py-2 text-sm text-gray-400 hover:text-white hover:bg-gray-700/60 rounded-lg transition-all"
        >
          <ExternalLink className="h-4 w-4 flex-shrink-0" />
          {!collapsed && <span>View Site</span>}
        </Link>
        <button
          onClick={logout}
          className="w-full flex items-center gap-2 px-3 py-2 text-sm text-red-400 hover:text-red-300 hover:bg-red-900/20 rounded-lg transition-all"
          data-testid="sidebar-logout"
        >
          <LogOut className="h-4 w-4 flex-shrink-0" />
          {!collapsed && <span>Logout</span>}
        </button>
      </div>
    </div>
  );

  return (
    <div className="min-h-screen bg-gray-50 flex">
      {/* Desktop Sidebar */}
      <aside
        className={`hidden lg:flex flex-col fixed top-0 left-0 h-screen bg-gray-900 z-30 transition-all duration-200 ${
          collapsed ? 'w-16' : 'w-56'
        }`}
      >
        {NavContent()}
        {/* Collapse Toggle */}
        <button
          onClick={() => setCollapsed(!collapsed)}
          className="absolute -right-3 top-20 w-6 h-6 bg-gray-700 hover:bg-gray-600 rounded-full flex items-center justify-center text-gray-300 shadow-md"
          data-testid="sidebar-collapse-btn"
        >
          {collapsed ? <ChevronRight className="h-3.5 w-3.5" /> : <ChevronLeft className="h-3.5 w-3.5" />}
        </button>
      </aside>

      {/* Mobile Sidebar Overlay */}
      {mobileOpen && (
        <div className="fixed inset-0 bg-black/50 z-40 lg:hidden" onClick={() => setMobileOpen(false)} />
      )}
      <aside
        className={`fixed top-0 left-0 h-screen w-56 bg-gray-900 z-50 transform transition-transform lg:hidden ${
          mobileOpen ? 'translate-x-0' : '-translate-x-full'
        }`}
      >
        {NavContent()}
      </aside>

      {/* Main Content */}
      <div className={`flex-1 transition-all duration-200 ${collapsed ? 'lg:ml-16' : 'lg:ml-56'}`}>
        {/* Top Bar */}
        <header className="bg-white shadow-sm sticky top-0 z-20">
          <div className="flex items-center justify-between px-4 py-3">
            <button
              onClick={() => setMobileOpen(true)}
              className="lg:hidden p-1.5 rounded-lg hover:bg-gray-100"
              data-testid="mobile-menu-btn"
            >
              <Menu className="h-5 w-5 text-gray-600" />
            </button>
            <div className="hidden lg:block">
              <h1 className="text-sm font-semibold text-gray-700 capitalize">
                {location.pathname.split('/').pop()?.replace('-', ' ') || 'Dashboard'}
              </h1>
            </div>
            <div className="flex items-center gap-3">
              <span className="text-xs text-gray-500 hidden sm:block">
                {currentUser?.full_name || currentUser?.username}
              </span>
              <span className="px-2 py-0.5 bg-green-100 text-green-700 rounded text-[10px] font-medium capitalize">
                {currentUser?.role?.replace('_', ' ')}
              </span>
            </div>
          </div>
        </header>

        {/* Page Content */}
        <main className="p-4 lg:p-6">
          {children}
        </main>
      </div>
    </div>
  );
};

export default AdminLayout;
